import { Copy, Eye, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { useFolderItemActions } from "src/components/NewComponents/menuActions/useFolderItemActions";
import {
	useCopyDeck,
	useRemoveDecks,
	useRestartKnowDontKnowDeck,
} from "src/hooks/useFlashcards";
import ChartIcon from "src/images/Icons/newIcons/chart.svg";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import EditIcon from "src/images/Icons/newIcons/edit.svg";
import FolderIcon from "src/images/Icons/newIcons/folder.svg";
import FlashcardIcon from "src/images/Icons/newIcons/searchTypes/fish.svg";

export function useDeckActions(row: any) {
	const { openModal } = useModals();
	const { t } = useTranslation();
	const history = useHistory();
	const folderActions = useFolderItemActions(row, "deck");
	const { mutateAsync: restartKnowDontKnowDeck } = useRestartKnowDontKnowDeck();
	const { mutateAsync: copyDeck } = useCopyDeck();
	const { mutateAsync: removeDecks } = useRemoveDecks();

	const resolveItem =
		row.flashcardsToResolveCount === 0
			? null
			: {
					title: t("menuActions.flashcards.resolve", "Rozwiąż zestaw"),
					testId: `deck-resolve-${row.code}`,
					onClick: () =>
						history.push(
							generatePath(routing.paths.flashcardsApp, { deckId: row.code }),
						),
					Icon: <FlashcardIcon className="icon-normal" />,
				};

	const resolveAgainItem =
		row.type === "know_dont_know"
			? {
					title: t("menuActions.flashcards.resolveAgain", "Rozwiąż ponownie"),
					testId: `deck-resolve-again-${row.code}`,
					onClick: async () => {
						await restartKnowDontKnowDeck(row.id);
						history.push(
							generatePath(routing.paths.flashcardsApp, {
								deckId: row.code,
							}),
						);
					},
					Icon: <RotateCcw className="icon-normal" />,
				}
			: null;

	const previewItem = {
		title: t("menuActions.flashcards.preview", "Podgląd zestawu"),
		testId: `deck-preview-${row.code}`,
		onClick: () => {
			openModal(ModalsNames.DeckPreviewModal, {
				deckId: row.id,
				deckName: row.name,
				deckCode: row.code,
				hideStudyButton: true,
			});
		},
		Icon: <Eye className="icon-normal" />,
	};

	const editItem = {
		title: t("menuActions.flashcards.edit", "Edytuj zestaw"),
		testId: `deck-edit-${row.code}`,
		onClick: () => {
			openModal(ModalsNames.EditDeckModal, {
				deckId: row.id,
				deckName: row.name,
				deckCode: row.code,
			});
		},
		Icon: <EditIcon className="icon-normal" />,
	};

	const statisticsItem = {
		title: t("menuActions.flashcards.statistics", "Statystyki"),
		testId: `deck-statistics-${row.code}`,
		onClick: () => {
			openModal(ModalsNames.DeckStatisticsModal, {
				deckId: row.id,
				deckType: row.type,
				createdAt: row.createdAt,
			});
		},
		Icon: <ChartIcon className="icon-normal" />,
	};

	const copyItem = {
		title: t("menuActions.flashcards.copy", "Skopiuj zestaw"),
		testId: `deck-copy-${row.code}`,
		onClick: async () => {
			const toastId = toast.loading(
				t("menuActions.flashcards.copying", "Kopiowanie zestawu…"),
				{ position: toast.POSITION.BOTTOM_LEFT },
			);
			try {
				const { code } = await copyDeck({ deckId: row.id });
				toast.dismiss(toastId);
				history.push(
					generatePath(routing.paths.flashcardsApp, { deckId: code }),
				);
			} catch {
				toast.update(toastId, {
					render: t("errors.somethingWentWrong", "Coś poszło nie tak"),
					type: "error",
					isLoading: false,
					pauseOnHover: false,
					autoClose: 3000,
				});
			}
		},
		Icon: <Copy className="icon-normal" />,
	};

	const moveToFolderItem = !row.folderId
		? {
			title: t("menuActions.flashcards.moveToFolder", "Przenieś do folderu"),
			testId: `deck-move-to-folder-${row.code}`,
			onClick: () => {
				openModal(ModalsNames.MoveToFolderModal, {
					selectedItems: [{ id: row.id, itemId: row.id, itemType: "deck" }],
					isCopyMode: true,
					multiSelect: true,
					custom: {
						title: t("folder.moveToFolderTitle", "Przenieś do folderu"),
						actionLabel: t("folder.move", "Przenieś"),
					},
					onSuccess: () => {
						toast.success(t("folder.movedToFolderSuccess", "Przeniesiono do folderu"));
					},
				});
			},
			Icon: <FolderIcon className="icon-normal" />,
		}
		: folderActions;

	const removeItem = {
		title: t("menuActions.flashcards.remove", "Usuń zestaw"),
		testId: `deck-remove-${row.code}`,
		className: "list-item-error",
		itemTextClassName: "text-error",
		onClick: () => {
			openModal(ModalsNames.DeleteItemModal, {
				title: t("modals.deleteItem.deck.title", "Usunąć zestaw?"),
				description: t(
					"modals.deleteItem.deck.description",
					"Zestaw zostanie trwale usunięty razem ze wszystkimi fiszkami. Tej operacji nie można cofnąć.",
				),
				confirmText: t("modals.deleteItem.deck.confirm", "Usuń zestaw"),
				itemLabel: row.name,
				onDelete: () => removeDecks([row.code]),
			});
		},
		Icon: <DeleteIcon className="icon-small icon-error" />,
	};

	const divider = { isDivider: true as const };

	return [
		resolveItem,
		resolveAgainItem,
		previewItem,
		statisticsItem,
		divider,
		editItem,
		copyItem,
		moveToFolderItem,
		divider,
		removeItem,
	].filter(Boolean);
}
